import { useState } from 'react'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import type { CustomerSummary } from '@webmono/storefront-ui/types'
import { formatCents, formatRelativeTime } from '@webmono/storefront-ui/utils'
import { useCustomers } from '@/lib/hooks'

function CustomerRow({ customer }: { customer: CustomerSummary }) {
  const tenant = useTenantConfig()

  return (
    <div className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-medium text-primary">
          {customer.name.charAt(0).toUpperCase()}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-foreground truncate">{customer.name}</h3>
        <p className="text-sm text-muted-foreground truncate">{customer.phone}</p>
      </div>

      <div className="hidden sm:block text-right">
        <p className="text-sm font-medium text-foreground">{customer.totalOrders} orders</p>
        <p className="text-xs text-muted-foreground">
          {customer.lastOrderAt ? formatRelativeTime(customer.lastOrderAt) : 'Never'}
        </p>
      </div>

      <div className="text-right">
        <p className="font-mono text-sm font-medium text-foreground">
          {formatCents(customer.lifetimeSpendCents, tenant.operations.currency)}
        </p>
        {customer.loyaltyTier && (
          <p className="text-xs text-muted-foreground">{customer.loyaltyTier}</p>
        )}
      </div>
    </div>
  )
}

export function MerchantCustomersPage() {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useCustomers({ search })

  const customers = data?.customers ?? []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold text-foreground">Customers</h1>
        <span className="text-sm text-muted-foreground">
          {data?.total ?? 0} total
        </span>
      </div>

      <div className="relative mb-6">
        <input
          type="search"
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-input bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border animate-pulse">
              <div className="w-10 h-10 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-1/3" />
                <div className="h-3 bg-muted rounded w-1/4" />
              </div>
              <div className="h-4 bg-muted rounded w-16" />
            </div>
          ))}
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-medium text-foreground mb-1">No customers found</h2>
          <p className="text-muted-foreground">
            {search ? 'Try a different search term' : 'Customers will appear here after their first order'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {customers.map((customer) => (
            <CustomerRow key={customer.id} customer={customer} />
          ))}
        </div>
      )}
    </div>
  )
}
